import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useGroups } from '@/app/redux/hooks/groups';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { setContactFormModalOpen } from '@/app/redux/slices/modalsSlice';
import { requireSessionUserDetails } from '@/app/redux/slices/sessionSlice';
import ActivationChecklist from '@/common/ui/ActivationChecklist';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import FeedbackModal from '@/common/ui/modals/FeedbackModal';
import { ProIcon } from '@/common/ui/UpgradeButton';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import { isUserAdmin } from '@/common/utils/organisation';
import changelogItems from '@/dashboard/Changelog/changelogItems';
import ChangelogSheet from '@/dashboard/Changelog/ChangelogSheet';
import {
  ChalkboardTeacherIcon,
  ChartLineIcon,
  CheckIcon,
  CreditCardIcon,
  GearIcon,
  HandWavingIcon,
  JoystickIcon,
  LightbulbIcon,
  PaperPlaneTiltIcon,
  PathIcon,
  PencilLineIcon,
  RowsIcon,
  SignOutIcon,
  SparkleIcon,
  SquaresFourIcon,
  StudentIcon,
  UserIcon,
  UsersIcon,
  UserSwitchIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertTitle } from '@shadcn/components/alert';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@shadcn/components/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from '@shadcn/components/sidebar';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import { ChevronDown, ChevronsUpDown, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleLogout } from '../utils/auth';
import trackMixpanelEvent from '../utils/mixpanel';
import HelpMenu from './HelpMenu';
import InitialsCircle from './InitialsCircle';
import UpgradeCard from './UpgradeCard';

interface SidebarItem {
  title: string;
  url: string;
  icon: React.ReactNode;
  requiredEntitlement?: string;
  beta?: boolean;
  openInNewTab?: boolean;
}

type BetaInfoKey = keyof typeof items;

type BetaInfoConfig = Record<
  BetaInfoKey,
  (handleOpenContactForm: () => void) => React.ReactNode
>;

const betaInfoConfig: BetaInfoConfig = {
  'Custom Levels': (handleOpenContactForm: () => void) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info size={14} />
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-[300px] leading-5">
          Custom levels allow you to select custom content for learners to play
          with in the game. This is a beta feature and will be updated
          regularly.
          <br />
          <br />
          If you encounter any issues, please{' '}
          <Button
            variant="link"
            onClick={handleOpenContactForm}
            className="font-medium text-[12px] h-fit p-0 m-0 "
          >
            get in touch.
          </Button>
        </p>
      </TooltipContent>
    </Tooltip>
  ),
};

const items: { [key: string]: SidebarItem[] } = {
  Dashboard: [
    {
      title: 'Learners',
      url: '/dashboard/learners',
      icon: <StudentIcon weight="light" />,
    },
    {
      title: 'Groups',
      url: '/dashboard/groups',
      icon: <SquaresFourIcon weight="light" />,
    },
  ],
  Tools: [
    {
      title: 'Assessments',
      url: '/dashboard/assessments',
      icon: <PencilLineIcon weight="light" />,
      requiredEntitlement: 'assessments',
    },
    {
      title: 'Lesson Plans',
      url: '/dashboard/lessonplans',
      icon: <ChalkboardTeacherIcon weight="light" />,
    },
    {
      title: 'Word Mats',
      url: '/dashboard/wordmats',
      icon: <RowsIcon size={16} weight="light" />,
    },
    {
      title: 'Custom Levels',
      url: '/dashboard/customlevels',
      icon: <JoystickIcon weight="light" />,
      beta: true,
      requiredEntitlement: 'custom-levels',
    },
    {
      title: 'Insights',
      url: '/dashboard/insights',
      icon: <ChartLineIcon weight="light" />,
      requiredEntitlement: 'insights',
    },
  ],
  Resources: [
    {
      title: 'Scope & Sequence',
      url: '/dashboard/scopeandsequence',
      icon: <PathIcon weight="light" />,
    },
  ],
};

export function AppSidebar() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebar = useSidebar();
  const isFeatureEnabled = useIsFeatureEnabled();

  const {
    organisation,
    isLoading,
    isFetching,
    allOrganisations,
    isLoadingAllOrgs,
    switchOrganisation,
  } = useOrganisation();

  const { uid: userId, email: userEmail } = useAppSelector(
    requireSessionUserDetails,
  );

  const { groups } = useGroups();

  const [isGroupsCollapsed, setGroupsCollapsed] = useState(false);
  const [groupsSortBy, setGroupsSortBy] = useState<'alpha' | 'recent'>(
    getFromLocalStorage('groups-sort') || 'alpha',
  );
  const [changelogSheetOpen, setChangelogSheetOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const [changelogLastSeenDate, setChangelogLastSeenDate] = useState<
    string | null
  >(() => getFromLocalStorage('changelog-last-seen-date'));
  const changelogLatestEntryDate = changelogItems[0]?.date ?? null;
  const changelogHasUnread = useMemo(() => {
    if (!changelogLatestEntryDate) return false;
    if (!changelogLastSeenDate) return true;
    return changelogLatestEntryDate > changelogLastSeenDate;
  }, [changelogLatestEntryDate, changelogLastSeenDate]);
  const markChangelogAsSeen = useCallback(() => {
    if (changelogLatestEntryDate) {
      saveToLocalStorage('changelog-last-seen-date', changelogLatestEntryDate);
      setChangelogLastSeenDate(changelogLatestEntryDate);
    }
  }, [changelogLatestEntryDate]);

  const showUpgradeCard =
    organisation?.license.plan === 'trial' ||
    organisation?.license.plan === 'free';
  const showActivationChecklist =
    organisation && !organisation.activationChecklist.isDismissed;

  const organisationName = organisation?.name ?? 'Unnamed Organisation';
  const userGroups = groups.filter((group) =>
    group.managerIds.includes(userId),
  );
  const sortedGroups = useMemo(() => {
    return userGroups.sort((a, b) => {
      if (groupsSortBy === 'recent') {
        return b.createdAt - a.createdAt;
      }
      if (groupsSortBy === 'alpha') {
        return a.name.localeCompare(b.name, undefined, { numeric: true });
      }
      return 0;
    });
  }, [userGroups, groupsSortBy]);

  useEffect(() => {
    saveToLocalStorage('groups-sort', groupsSortBy);
  }, [groupsSortBy]);

  const getBetaInfo = (title: BetaInfoKey): React.ReactNode => {
    const handleOpenContactForm = () => dispatch(setContactFormModalOpen(true));
    return betaInfoConfig[title](handleOpenContactForm);
  };

  function getIsActive(url: string, exact = false) {
    if (exact) {
      return location.pathname === url;
    }
    // Use startsWith for prefix matching to avoid false positives
    // e.g. /dashboard/wordmats should not match /wordmat
    return location.pathname === url || location.pathname.startsWith(url + '/');
  }

  const handleOpenChangelog = () => {
    setChangelogSheetOpen(true);
    markChangelogAsSeen();
    trackMixpanelEvent('Opened Changelog Sheet', { source: 'sidebar' });
  };

  function handleSwitchOrganisation(organisationId: string) {
    if (organisationId !== organisation?.id) {
      navigate(getDefaultDashboardRoute());
      switchOrganisation(organisationId);
    }
  }

  return (
    <Sidebar collapsible="icon" className="pt-2">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton>
                  {isLoading || isFetching ? (
                    <>
                      <Skeleton className="h-6 w-6 rounded-full shrink-0" />
                      <Skeleton className="h-4 w-full" />
                    </>
                  ) : (
                    <>
                      <InitialsCircle
                        userName={organisationName || 'UO'}
                        size={24}
                        className={cn(
                          sidebar.state === 'collapsed' && '-ml-0.5',
                        )}
                      />
                      <span className="w-full overflow-hidden text-ellipsis text-nowrap">
                        {organisationName}
                      </span>
                      <ChevronsUpDown className="ml-auto" />
                    </>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                className={cn(
                  'w-[--radix-popper-anchor-width] min-w-[240px]',
                  sidebar.state === 'collapsed' && 'ml-2',
                )}
              >
                {!organisationName && (
                  <Alert variant="warning" className="p-1 mb-2">
                    <WarningCircleIcon size={16} weight="bold" />
                    <AlertTitle>Unnamed Organisation</AlertTitle>
                    <AlertDescription>
                      Set a name in your organisation settings.
                    </AlertDescription>
                  </Alert>
                )}
                <DropdownMenuItem
                  onClick={() => navigate(`/dashboard/settings/profile`)}
                >
                  <UserIcon size={16} />
                  <span className="ml-2">Account settings</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/settings/organisation')}
                >
                  <GearIcon size={16} />
                  <span className="ml-2">Organisation settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/settings/members')}
                >
                  <UsersIcon size={16} />
                  <span className="ml-2">Manage & invite members</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate(`/dashboard/settings/billing`)}
                >
                  <CreditCardIcon size={16} />
                  <span className="ml-2">Billing & subscription</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigate(`/dashboard/settings/learner-settings`)
                  }
                >
                  <StudentIcon size={16} />
                  <span className="ml-2">Default learner settings</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <UserSwitchIcon size={16} />
                    <span className="ml-2">Switch organisations</span>
                  </DropdownMenuSubTrigger>

                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="ml-1">
                      <DropdownMenuLabel className="text-neutral-500 font-semibold">
                        {userEmail}
                      </DropdownMenuLabel>

                      {isLoadingAllOrgs
                        ? Array.from({ length: 3 }).map((_, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 px-2 py-1.5"
                            >
                              <Skeleton className="h-6 w-6 rounded-full shrink-0" />
                              <Skeleton className="h-4 w-full" />
                            </div>
                          ))
                        : allOrganisations.map((org) => (
                            <DropdownMenuItem
                              key={org.id}
                              onClick={() => handleSwitchOrganisation(org.id)}
                              className="min-w-[180px]"
                            >
                              <InitialsCircle userName={org.name} size={24} />
                              <span className="ml-2 flex w-full items-center justify-between">
                                {org.name}
                                {org.id === organisation?.id && (
                                  <CheckIcon size={16} />
                                )}
                              </span>
                            </DropdownMenuItem>
                          ))}

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() =>
                          navigate('/dashboard/settings/my-organisations')
                        }
                      >
                        {/* <UsersThree size={18} weight="bold" className="mx-1" /> */}
                        <span className="ml-2 font-medium">
                          View all organisations
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>

                <DropdownMenuItem onClick={handleLogout}>
                  <SignOutIcon size={16} />
                  <span className="ml-2">Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {Object.entries(items).map(([group, items]) => (
          <SidebarGroup key={group}>
            <SidebarGroupLabel>{group}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(
                  ({
                    title,
                    url,
                    icon,
                    requiredEntitlement,
                    beta,
                    openInNewTab,
                  }) => {
                    return title === 'Groups' && sortedGroups.length > 0 ? (
                      <Collapsible
                        className="group/collapsible"
                        key={title}
                        open={!isGroupsCollapsed}
                        onOpenChange={(isOpen) => setGroupsCollapsed(!isOpen)}
                      >
                        <SidebarMenuItem className="w-full">
                          <SidebarMenuButton
                            isActive={getIsActive(url, true)}
                            onClick={() => navigate(url)}
                            tooltip={title}
                          >
                            {icon}
                            <span>{title}</span>
                            {!isGroupsCollapsed && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <GearIcon
                                    className="ml-auto p-0.5"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenuLabel>Sort</DropdownMenuLabel>
                                  <DropdownMenuItem
                                    className="gap-4 justify-between"
                                    onClick={() => setGroupsSortBy('recent')}
                                  >
                                    <span>Recently added</span>
                                    {groupsSortBy === 'recent' && (
                                      <CheckIcon className="w-4 h-4" />
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="gap-4 justify-between"
                                    onClick={() => setGroupsSortBy('alpha')}
                                  >
                                    <span>Alphabetical</span>
                                    {groupsSortBy === 'alpha' && (
                                      <CheckIcon className="w-4 h-4" />
                                    )}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <CollapsibleTrigger asChild>
                              <ChevronDown
                                className={`transition-transform h-4 w-4 ${
                                  isGroupsCollapsed
                                    ? 'rotate-[-90deg] ml-auto'
                                    : 'rotate-0'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </CollapsibleTrigger>
                          </SidebarMenuButton>
                          <CollapsibleContent className="w-full">
                            <SidebarMenuSub className="mr-0 pr-0">
                              {sortedGroups.map((group) => (
                                <SidebarMenuItem key={group.id}>
                                  <SidebarMenuButton
                                    onClick={() =>
                                      navigate(`/dashboard/groups/${group.id}`)
                                    }
                                    isActive={getIsActive(
                                      `/dashboard/groups/${group.id}`,
                                    )}
                                  >
                                    <InitialsCircle
                                      userName={group.name}
                                      size={20}
                                    />
                                    <span>{group.name}</span>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    ) : (
                      <SidebarMenuItem key={title}>
                        <SidebarMenuButton
                          className="flex items-center"
                          onClick={() => {
                            if (openInNewTab) {
                              window.open(url, '_blank');
                            } else {
                              navigate(url);
                            }
                          }}
                          isActive={getIsActive(url)}
                          tooltip={title}
                        >
                          {icon}
                          <span>{title}</span>
                          {requiredEntitlement &&
                            !isFeatureEnabled(requiredEntitlement) && (
                              <ProIcon />
                            )}
                          {beta && (
                            <Badge
                              variant="outline"
                              className="ml-auto flex items-center gap-2"
                            >
                              Beta {getBetaInfo(title as BetaInfoKey)}
                            </Badge>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  },
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {showUpgradeCard &&
          !showActivationChecklist &&
          sidebar.state === 'expanded' && <UpgradeCard />}
        {showActivationChecklist && sidebar.state === 'expanded' && (
          <ActivationChecklist />
        )}
        <SidebarMenu>
          {/* Invite Team Members (admin only) */}
          {isUserAdmin(organisation, userId) && (
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => {
                  trackMixpanelEvent('Clicked Invite Team Members', {
                    source: 'sidebar',
                  });
                  navigate('/dashboard/settings/members');
                }}
                tooltip="Invite Team Members"
              >
                <PaperPlaneTiltIcon weight="light" />
                <span>Invite Team Members</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* Feature Requests */}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => {
                trackMixpanelEvent('Clicked Feature Requests', {
                  source: 'sidebar',
                });
                navigate('/dashboard/feature-requests');
              }}
              isActive={getIsActive('/dashboard/feature-requests')}
              tooltip="Feature Requests"
            >
              <LightbulbIcon weight="light" />
              <span>Feature Requests</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* What's new */}
          <SidebarMenuItem>
            <SidebarMenuButton
              className={cn(
                changelogHasUnread &&
                  'text-purple-600 font-semibold hover:text-purple-600',
              )}
              onClick={handleOpenChangelog}
              tooltip="What's new"
            >
              <SparkleIcon
                weight="light"
                className={cn(changelogHasUnread && 'text-purple-500')}
              />
              <span>What's new</span>
              {changelogHasUnread && sidebar.state === 'expanded' && (
                <span className="ml-auto h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_6px_2px_rgba(168,85,247,0.5)]" />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Give Feedback */}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => {
                trackMixpanelEvent('Clicked Give Feedback', {
                  source: 'sidebar',
                });
                setFeedbackModalOpen(true);
              }}
              tooltip="Give Feedback"
            >
              <HandWavingIcon weight="light" />
              <span>Give Feedback</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Need Help? */}
          <HelpMenu />
        </SidebarMenu>
      </SidebarFooter>

      <ChangelogSheet
        open={changelogSheetOpen}
        onOpenChange={setChangelogSheetOpen}
      />

      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </Sidebar>
  );
}
